import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import RootFractionInput, {
  formatLatexPreview,
  parseRootFractionInput,
} from "../../../../components/inputs/RootFractionInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function parseVal(v) {
  if (v && typeof v === "object" && Number.isFinite(v.parsed)) return v.parsed;
  if (v && typeof v === "object" && typeof v.raw === "string") {
    const parsed = parseRootFractionInput(v.raw);
    if (typeof parsed === "number" && Number.isFinite(parsed)) return parsed;
  }
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const parsed = parseRootFractionInput(v);
    if (typeof parsed === "number" && Number.isFinite(parsed)) return parsed;
  }
  return NaN;
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) [x, y] = [y, x % y];
  return x || 1;
}

function reduceFraction(num, den) {
  if (den < 0) return reduceFraction(-num, -den);
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

function fmtLinTerm(coef, variable, isFirst = false) {
  if (coef === 0) return "";
  const abs = Math.abs(coef);
  const core = abs === 1 ? `${variable}` : `${abs}${variable}`;
  if (isFirst) return coef < 0 ? `-${core}` : core;
  return coef < 0 ? `- ${core}` : `+ ${core}`;
}

function fmtConst(c, hasPrev) {
  if (c === 0) return hasPrev ? "" : "0";
  if (!hasPrev) return `${c}`;
  return c < 0 ? `- ${Math.abs(c)}` : `+ ${c}`;
}

function lineStdLatex(A, B, C) {
  const parts = [];
  const t1 = fmtLinTerm(A, "x", true);
  if (t1) parts.push(t1);
  const t2 = fmtLinTerm(B, "y", parts.length === 0);
  if (t2) parts.push(t2);
  const t3 = fmtConst(C, parts.length > 0);
  if (t3) parts.push(t3);
  return `${parts.join(" ")} = 0`;
}

function lineSlopeLatex(A, B, C) {
  // A x + B y + C = 0, B != 0
  const p = -A;
  const q = -C;
  const d = B;
  const pR = reduceFraction(p, d);
  const qR = reduceFraction(q, d);

  const mPart =
    pR.num === 0
      ? ""
      : pR.den === 1
      ? pR.num === 1
        ? "x"
        : pR.num === -1
        ? "-x"
        : `${pR.num}x`
      : `\\frac{${pR.num}}{${pR.den}}x`;

  const bPart =
    qR.num === 0
      ? ""
      : qR.den === 1
      ? `${qR.num > 0 ? "+ " : "- "}${Math.abs(qR.num)}`
      : `${qR.num > 0 ? "+ " : "- "}\\frac{${Math.abs(qR.num)}}{${qR.den}}`;

  if (!mPart) return `y = ${qR.den === 1 ? qR.num : `\\frac{${qR.num}}{${qR.den}}`}`;
  return `y = ${mPart} ${bPart}`.trim();
}

function circleLatex(cx, cy, r2) {
  const xPart = cx === 0 ? "x^2" : `(x ${cx > 0 ? "-" : "+"} ${Math.abs(cx)})^2`;
  const yPart = cy === 0 ? "y^2" : `(y ${cy > 0 ? "-" : "+"} ${Math.abs(cy)})^2`;
  return `${xPart} + ${yPart} = ${r2}`;
}

function buildWindow(center, span = 10) {
  let start = Math.round(center) - span / 2;
  start = Math.max(-9, Math.min(-1, start));
  return [start, start + span];
}

function formatValue(value) {
  return Number.isInteger(value)
    ? value.toString()
    : value.toFixed(2).replace(/\.?0+$/, "");
}

function formatRational(num, den) {
  if (den < 0) return formatRational(-num, -den);
  const reduced = reduceFraction(num, den);
  const n = reduced.num;
  const d = reduced.den;
  if (n === 0) return "0";
  if (d === 1) return `${n}`;

  const sign = n < 0 ? "-" : "";
  const absN = Math.abs(n);

  // Preferujemy czytelny zapis mieszany dla ułamków niewłaściwych.
  if (absN > d) {
    const whole = Math.floor(absN / d);
    const rem = absN % d;
    if (rem === 0) return `${sign}${whole}`;
    return `${sign}${whole}\\tfrac{${rem}}{${d}}`;
  }

  return `${sign}\\dfrac{${absN}}{${d}}`;
}

function simplifySqrt(n) {
  if (n <= 0) return { outside: 0, inside: 1 };
  let outside = 1;
  let inside = n;
  for (let i = 2; i * i <= inside; i++) {
    while (inside % (i * i) === 0) {
      inside /= i * i;
      outside *= i;
    }
  }
  return { outside, inside };
}

function simplifyFractionWithSqrtProper(numerConst, sqrtCoeff, denom, sqrtInner) {
  if (denom < 0) {
    return simplifyFractionWithSqrtProper(-numerConst, -sqrtCoeff, -denom, sqrtInner);
  }
  const g = gcd(gcd(Math.abs(numerConst), Math.abs(sqrtCoeff)), Math.abs(denom));
  return {
    numerConst: numerConst / g,
    sqrtCoeff: sqrtCoeff / g,
    denom: denom / g,
    sqrtInner,
  };
}

function formatFractionWithSqrtProper({ numerConst, sqrtCoeff, denom, sqrtInner }) {
  if (!sqrtInner || sqrtInner === 1) {
    const value = numerConst + sqrtCoeff * (sqrtInner === 1 ? 1 : 0);
    return formatRational(value, denom);
  }

  const parts = [];
  if (numerConst !== 0) parts.push(numerConst.toString());

  if (sqrtCoeff !== 0) {
    const root =
      sqrtCoeff === 1
        ? `\\sqrt{${sqrtInner}}`
        : sqrtCoeff === -1
        ? `-\\sqrt{${sqrtInner}}`
        : `${sqrtCoeff}\\sqrt{${sqrtInner}}`;
    const sign = sqrtCoeff > 0 && numerConst !== 0 ? "+" : "";
    parts.push(`${sign}${root}`);
  }

  if (parts.length === 0) return "0";
  const numerator = parts.join(" ");
  return denom === 1 ? numerator : `\\dfrac{${numerator}}{${denom}}`;
}

function makeTaskData() {
  for (let tries = 0; tries < 600; tries += 1) {
    const c1x = randInt(-4, 4);
    const c1y = randInt(-4, 4);
    const c2x = randInt(-4, 4);
    const c2y = randInt(-4, 4);
    if (c1x === c2x && c1y === c2y) continue;

    const r1 = randInt(2, 6);
    const r2 = randInt(2, 6);
    const r1sq = r1 * r1;
    const r2sq = r2 * r2;

    const dx = c2x - c1x;
    const dy = c2y - c1y;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (!(Math.abs(r1 - r2) < d && d < r1 + r2)) continue; // dwa punkty

    // prosta potęgowa
    const A = 2 * (c2x - c1x);
    const B = 2 * (c2y - c1y);
    const C = c1x * c1x + c1y * c1y - r1sq - c2x * c2x - c2y * c2y + r2sq;

    if (A === 0 || B === 0) continue;

    // y = (p*x + q) / dDen
    const p = -A;
    const q = -C;
    const dDen = B;
    const k = q - c1y * dDen;

    const QA = dDen * dDen + p * p;
    const QB = -2 * c1x * dDen * dDen + 2 * p * k;
    const QC = c1x * c1x * dDen * dDen + k * k - r1sq * dDen * dDen;

    // Upraszczamy trójmian kwadratowy przez NWD(A,B,C),
    // żeby rachunki (w tym delta) były bardziej "ludzkie".
    const gABC = gcd(gcd(Math.abs(QA), Math.abs(QB)), Math.abs(QC));
    const QAred = QA / gABC;
    const QBred = QB / gABC;
    const QCred = QC / gABC;

    const Delta = QBred * QBred - 4 * QAred * QCred;
    if (Delta <= 0) continue;
    if (Math.abs(Delta) > 20000) continue;

    const sqrtDelta = Math.sqrt(Delta);
    const x1 = (-QBred - sqrtDelta) / (2 * QAred);
    const x2 = (-QBred + sqrtDelta) / (2 * QAred);
    const y1 = (p * x1 + q) / dDen;
    const y2 = (p * x2 + q) / dDen;

    if (![x1, y1, x2, y2].every(Number.isFinite)) continue;

    const sqrt = simplifySqrt(Delta);
    if (sqrt.inside > 100) continue;
    const rawConst = -QBred;
    const denom = 2 * QAred;

    const x1Exact = simplifyFractionWithSqrtProper(
      rawConst,
      -sqrt.outside,
      denom,
      sqrt.inside
    );
    const x2Exact = simplifyFractionWithSqrtProper(
      rawConst,
      +sqrt.outside,
      denom,
      sqrt.inside
    );

    const y1Exact = simplifyFractionWithSqrtProper(
      p * x1Exact.numerConst + q * x1Exact.denom,
      p * x1Exact.sqrtCoeff,
      x1Exact.denom * dDen,
      x1Exact.sqrtInner
    );
    const y2Exact = simplifyFractionWithSqrtProper(
      p * x2Exact.numerConst + q * x2Exact.denom,
      p * x2Exact.sqrtCoeff,
      x2Exact.denom * dDen,
      x2Exact.sqrtInner
    );

    const pt1 = {
      x: x1,
      y: y1,
      label: "A",
      xLatex: formatFractionWithSqrtProper(x1Exact),
      yLatex: formatFractionWithSqrtProper(y1Exact),
      xExact: x1Exact,
      yExact: y1Exact,
      showApprox: x1Exact.sqrtInner !== 1 || y1Exact.sqrtInner !== 1,
    };
    const pt2 = {
      x: x2,
      y: y2,
      label: "B",
      xLatex: formatFractionWithSqrtProper(x2Exact),
      yLatex: formatFractionWithSqrtProper(y2Exact),
      xExact: x2Exact,
      yExact: y2Exact,
      showApprox: x2Exact.sqrtInner !== 1 || y2Exact.sqrtInner !== 1,
    };

    const pts = [pt1, pt2].sort((u, v) => (u.x === v.x ? u.y - v.y : u.x - v.x));

    const keyX = [pts[0].x, pts[1].x, c1x, c2x];
    const keyY = [pts[0].y, pts[1].y, c1y, c2y];
    const xRange = buildWindow((Math.min(...keyX) + Math.max(...keyX)) / 2, 10);
    const yRange = buildWindow((Math.min(...keyY) + Math.max(...keyY)) / 2, 10);

    const inInner = (pt) =>
      pt.x >= xRange[0] + 1 &&
      pt.x <= xRange[1] - 1 &&
      pt.y >= yRange[0] + 1 &&
      pt.y <= yRange[1] - 1;

    if (!pts.every(inInner)) continue;

    return {
      circle1: { x: c1x, y: c1y, r2: r1sq },
      circle2: { x: c2x, y: c2y, r2: r2sq },
      line: { A, B, C },
      quad: {
        A: QAred,
        B: QBred,
        C: QCred,
        delta: Delta,
        rawA: QA,
        rawB: QB,
        rawC: QC,
        gcd: gABC,
      },
      points: pts,
      circle1Latex: circleLatex(c1x, c1y, r1sq),
      circle2Latex: circleLatex(c2x, c2y, r2sq),
      lineStdLatex: lineStdLatex(A, B, C),
      lineSlopeLatex: lineSlopeLatex(A, B, C),
      lineExpr: `y=(${p}*x+${q})/${dDen}`,
      xRange,
      yRange,
    };
  }

  // fallback
  return {
    circle1: { x: 0, y: 0, r2: 25 },
    circle2: { x: 2, y: 2, r2: 17 },
    line: { A: 4, B: 4, C: -8 },
    quad: { A: 2, B: -8, C: 6, delta: 16 },
    points: [
      { x: 1, y: 1, label: "A" },
      { x: 3, y: -1, label: "B" },
    ],
    circle1Latex: "x^2 + y^2 = 25",
    circle2Latex: "(x - 2)^2 + (y - 2)^2 = 17",
    lineStdLatex: "4x + 4y - 8 = 0",
    lineSlopeLatex: "y = -x + 2",
    lineExpr: "y=-x+2",
    xRange: [-6, 4],
    yRange: [-6, 4],
  };
}

export function generateTask() {
  return {
    type: "FindCircleCircleIntersectionPointsTask",
    data: makeTaskData(),
  };
}

export function renderTask(task) {
  const data = task.data ?? task;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>Dane okręgi przecinają się. Wyznacz punkty przecięcia:</p>
      <BlockMath math={data.circle1Latex} />
      <BlockMath math={data.circle2Latex} />
    </div>
  );
}

export function renderInput(taskOrCtx, userAnswerOrSet, onChange, disabled) {
  const legacy = typeof userAnswerOrSet === "function" && onChange === undefined;
  const userAnswer = legacy ? taskOrCtx?.userInput || {} : userAnswerOrSet || {};
  const setAnswer = legacy ? userAnswerOrSet : onChange;

  const setField = (k, v) => setAnswer({ ...userAnswer, [k]: v });

  return (
    <div className="flex flex-col gap-3">
      <div>
        <InlineMath math={"\\mathrm{P}_1"} />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math={"x_1 ="} />
        <RootFractionInput
          value={userAnswer?.x1 || ""}
          onChange={(v) => setField("x1", v)}
          disabled={disabled}
          label="x_1"
          placeholder="np. -1"
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math={"y_1 ="} />
        <RootFractionInput
          value={userAnswer?.y1 || ""}
          onChange={(v) => setField("y1", v)}
          disabled={disabled}
          label="y_1"
          placeholder="np. 2"
        />
      </div>
      <div style={{ marginTop: "0.25rem" }}>
        <InlineMath math={"\\mathrm{P}_2"} />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math={"x_2 ="} />
        <RootFractionInput
          value={userAnswer?.x2 || ""}
          onChange={(v) => setField("x2", v)}
          disabled={disabled}
          label="x_2"
          placeholder="np. 3"
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math={"y_2 ="} />
        <RootFractionInput
          value={userAnswer?.y2 || ""}
          onChange={(v) => setField("y2", v)}
          disabled={disabled}
          label="y_2"
          placeholder="np. -4"
        />
      </div>
    </div>
  );
}

export function checkAnswer(task, userAnswer) {
  const data = task.data ?? task;
  const pts = data.points;
  const eps = 0.05;

  const ux1 = parseVal(userAnswer?.x1);
  const uy1 = parseVal(userAnswer?.y1);
  const ux2 = parseVal(userAnswer?.x2);
  const uy2 = parseVal(userAnswer?.y2);

  if (
    !Number.isFinite(ux1) ||
    !Number.isFinite(uy1) ||
    !Number.isFinite(ux2) ||
    !Number.isFinite(uy2)
  ) {
    return { correct: false };
  }

  const pA = pts[0];
  const pB = pts[1];

  const direct =
    Math.abs(ux1 - pA.x) < eps &&
    Math.abs(uy1 - pA.y) < eps &&
    Math.abs(ux2 - pB.x) < eps &&
    Math.abs(uy2 - pB.y) < eps;

  const swapped =
    Math.abs(ux1 - pB.x) < eps &&
    Math.abs(uy1 - pB.y) < eps &&
    Math.abs(ux2 - pA.x) < eps &&
    Math.abs(uy2 - pA.y) < eps;

  return { correct: direct || swapped };
}

export function renderAnswer(task) {
  const data = task.data ?? task;
  const p1 = data.points[0];
  const p2 = data.points[1];
  return (
    <div style={{ textAlign: "left", lineHeight: 1.35 }}>
      <div style={{ marginBottom: "0.5rem" }}>
        <InlineMath math={`\\mathrm{P}_1 = \\left(${p1.xLatex},\\ ${p1.yLatex}\\right)`} />
      </div>
      <div>
        <InlineMath math={`\\mathrm{P}_2 = \\left(${p2.xLatex},\\ ${p2.yLatex}\\right)`} />
      </div>
      {(p1.showApprox || p2.showApprox) && (
        <div style={{ marginTop: "0.4rem" }}>
          <InlineMath
            math={`\\mathrm{P}_1 \\approx (${formatValue(p1.x)},\\ ${formatValue(
              p1.y
            )}),\\quad \\mathrm{P}_2 \\approx (${formatValue(p2.x)},\\ ${formatValue(
              p2.y
            )})`}
          />
        </div>
      )}
    </div>
  );
}

export function renderUserAnswer(taskOrUserAnswer, maybeUserAnswer) {
  const userAnswer =
    maybeUserAnswer && typeof maybeUserAnswer === "object"
      ? maybeUserAnswer
      : taskOrUserAnswer && typeof taskOrUserAnswer === "object"
      ? taskOrUserAnswer
      : {};

  const hasValue = (v) => {
    if (v == null) return false;
    if (typeof v === "number") return Number.isFinite(v);
    if (typeof v === "string") return v.trim() !== "";
    if (typeof v === "object") {
      if (typeof v.raw === "string") return v.raw.trim() !== "";
      if (typeof v.parsed === "number") return Number.isFinite(v.parsed);
    }
    return false;
  };

  const hasAny =
    hasValue(userAnswer?.x1) ||
    hasValue(userAnswer?.y1) ||
    hasValue(userAnswer?.x2) ||
    hasValue(userAnswer?.y2);

  if (!hasAny) return <i>(brak odpowiedzi)</i>;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.35 }}>
      <div style={{ marginBottom: "0.35rem" }}>
        <InlineMath
          math={`\\mathrm{P}_1=\\left(${formatLatexPreview(
            userAnswer?.x1?.raw || "?",
            "x_1"
          )},\\ ${formatLatexPreview(userAnswer?.y1?.raw || "?", "y_1")}\\right)`}
        />
      </div>
      <div>
        <InlineMath
          math={`\\mathrm{P}_2=\\left(${formatLatexPreview(
            userAnswer?.x2?.raw || "?",
            "x_2"
          )},\\ ${formatLatexPreview(userAnswer?.y2?.raw || "?", "y_2")}\\right)`}
        />
      </div>
    </div>
  );
}

export function renderExplanation(task) {
  const data = task.data ?? task;
  const { circle1, circle2, line, quad, points } = data;
  const { x: a1, y: b1, r2: r1sq } = circle1;
  const { x: a2, y: b2, r2: r2sq } = circle2;
  const { A, B, C } = line;
  const q = quad;
  const p1 = points[0];
  const p2 = points[1];
  const r1 = Math.sqrt(r1sq);
  const r2 = Math.sqrt(r2sq);

  const wrapIfNegative = (n) => (n < 0 ? `(${n})` : `${n}`);
  const pushTerm = (arr, coef, variable = "") => {
    if (coef === 0) return;
    const abs = Math.abs(coef);
    const value = variable ? (abs === 1 ? variable : `${abs}${variable}`) : `${abs}`;
    if (arr.length === 0) {
      arr.push(`${coef < 0 ? "-" : ""}${value}`);
    } else {
      arr.push(`${coef < 0 ? "-" : "+"} ${value}`);
    }
  };

  const expandedCircleLhs = (cx, cy) => {
    const terms = [];
    pushTerm(terms, 1, "x^2");
    pushTerm(terms, -2 * cx, "x");
    pushTerm(terms, cx * cx);
    pushTerm(terms, 1, "y^2");
    pushTerm(terms, -2 * cy, "y");
    pushTerm(terms, cy * cy);
    return terms.length ? terms.join(" ") : "0";
  };

  const quadTerms = [];
  pushTerm(quadTerms, q.A, "x^2");
  pushTerm(quadTerms, q.B, "x");
  pushTerm(quadTerms, q.C);
  const quadLatex = `${quadTerms.join(" ")} = 0`;
  const quadRawTerms = [];
  pushTerm(quadRawTerms, q.rawA, "x^2");
  pushTerm(quadRawTerms, q.rawB, "x");
  pushTerm(quadRawTerms, q.rawC);
  const quadRawLatex = `${quadRawTerms.join(" ")} = 0`;

  // Sztywny margines 1 kratki wokół okręgów.
  const xRange = [
    Math.floor(Math.min(a1 - r1, a2 - r2)) - 1,
    Math.ceil(Math.max(a1 + r1, a2 + r2)) + 1,
  ];
  const yRange = [
    Math.floor(Math.min(b1 - r1, b2 - r2)) - 1,
    Math.ceil(Math.max(b1 + r1, b2 + r2)) + 1,
  ];

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1:</strong> Odejmujemy stronami równania okręgów. Otrzymujemy równanie
        prostej potęgowej:
      </p>
      <BlockMath
        math={`\\begin{aligned}
${data.circle1Latex}\\\\
${data.circle2Latex}
\\end{aligned}`}
      />
      <BlockMath
        math={`\\begin{aligned}
${expandedCircleLhs(a1, b1)} &= ${r1sq}\\\\
${expandedCircleLhs(a2, b2)} &= ${r2sq}
\\end{aligned}`}
      />
      <BlockMath math={`\\text{Odejmujemy stronami (lewa od lewej, prawa od prawej):}`} />
      <BlockMath math={data.lineStdLatex} />
      <BlockMath math={data.lineSlopeLatex} />

      <p>
        <strong>Krok 2:</strong> Podstawiamy to równanie prostej do pierwszego okręgu:
      </p>
      <BlockMath math={data.circle1Latex} />
      <BlockMath math={`${data.lineSlopeLatex}`} />
      {q.gcd > 1 ? (
        <>
          <BlockMath math={`${quadRawLatex}\\ \\big|\\ :${q.gcd}`} />
          <BlockMath math={quadLatex} />
        </>
      ) : (
        <BlockMath math={quadRawLatex} />
      )}

      <p>
        <strong>Krok 3:</strong> Rozwiązujemy równanie kwadratowe:
      </p>
      <BlockMath
        math={`\\Delta = ${wrapIfNegative(q.B)}^2 - 4\\cdot${wrapIfNegative(
          q.A
        )}\\cdot${wrapIfNegative(q.C)} = ${formatValue(q.delta)}`}
      />
      <BlockMath math={`x_1 = ${p1.xLatex},\\quad x_2 = ${p2.xLatex}`} />
      <BlockMath math={`y_1 = ${p1.yLatex},\\quad y_2 = ${p2.yLatex}`} />
      {(p1.showApprox || p2.showApprox) && (
        <>
          <BlockMath
            math={`x_1 \\approx ${formatValue(p1.x)},\\quad x_2 \\approx ${formatValue(
              p2.x
            )}`}
          />
          <BlockMath
            math={`y_1 \\approx ${formatValue(p1.y)},\\quad y_2 \\approx ${formatValue(
              p2.y
            )}`}
          />
        </>
      )}

      <div style={{ textAlign: "center", marginTop: "0.5rem" }}>
        <p>
          <strong>Odpowiedź:</strong>
        </p>
        <div style={{ marginTop: "0.15rem" }}>
          <InlineMath math={`\\mathrm{P}_1=\\left(${p1.xLatex},\\ ${p1.yLatex}\\right)`} />
        </div>
        <div style={{ marginTop: "0.25rem" }}>
          <InlineMath math={`\\mathrm{P}_2=\\left(${p2.xLatex},\\ ${p2.yLatex}\\right)`} />
        </div>
      </div>
      {(p1.showApprox || p2.showApprox) && (
        <p>
          <strong>W przybliżeniu:</strong>{" "}
          <InlineMath
            math={`\\mathrm{P}_1\\approx(${formatValue(p1.x)},${formatValue(
              p1.y
            )}),\\quad \\mathrm{P}_2\\approx(${formatValue(p2.x)},${formatValue(
              p2.y
            )})`}
          />
          .
        </p>
      )}

      <div style={{ marginTop: "2rem", display: "flex", justifyContent: "center" }}>
        <GraphViewer
          width={500}
          xRange={xRange}
          yRange={yRange}
          equations={[{ expr: data.lineExpr, color: "#9d4edd" }]}
          circles={[
            {
              center: { x: a1, y: b1 },
              radius: r1,
              color: "#ff8c1a",
              label: "k_1",
            },
            {
              center: { x: a2, y: b2 },
              radius: r2,
              color: "#ff4fa3",
              label: "k_2",
            },
          ]}
          points={{
            P1: {
              x: p1.x,
              y: p1.y,
              label: "P1",
              color: "#cc5500",
              textColor: "#cc5500",
              textPosition: "top center",
            },
            P2: {
              x: p2.x,
              y: p2.y,
              label: "P2",
              color: "#cc5500",
              textColor: "#cc5500",
              textPosition: "top center",
            },
          }}
        />
      </div>
    </div>
  );
}

